import React from "react";
import moment from "moment";
import { getLocalizedString } from "../../shared/translation";
import { months } from "../../shared/dropdownLists";
import { SvgIcon } from "../../containers/Shared/SvgIcon";

export const QIMonthYearRangePicker = ({ selectedRange, setSelectedRange, formik }) => {
  const years = Array.from({ length: 10 }, (_, i) => moment().year() - i);

  // Guard to avoid redundant updates
  const setRangeIfChanged = (nextStart, nextEnd) => {
    const sameStart = selectedRange?.startDate && nextStart?.isSame(selectedRange.startDate);
    const sameEnd = selectedRange?.endDate && nextEnd?.isSame(selectedRange.endDate);
    if (!sameStart || !sameEnd) {
      setSelectedRange({ startDate: nextStart, endDate: nextEnd });
      return true;
    }
    return false;
  };

  // Get validation errors from formik
  const startMonthError =
    formik?.touched?.metadata?.start_month && formik?.errors?.metadata?.start_month;
  const startYearError =
    formik?.touched?.metadata?.start_year && formik?.errors?.metadata?.start_year;
  const endMonthError = formik?.touched?.metadata?.end_month && formik?.errors?.metadata?.end_month;
  const endYearError = formik?.touched?.metadata?.end_year && formik?.errors?.metadata?.end_year;

  // Enhanced validation function with logging for debugging
  const validateDateRange = (newStartDate, newEndDate) => {
    // Ensure we have valid moment objects
    if (
      !newStartDate ||
      !newEndDate ||
      !moment.isMoment(newStartDate) ||
      !moment.isMoment(newEndDate)
    ) {
      console.error("Invalid date objects provided to validateDateRange", {
        newStartDate,
        newEndDate,
      });
      return newEndDate;
    }

    // Compare years first
    if (newEndDate.year() < newStartDate.year()) {
      return newStartDate.clone();
    }
    // If same year, check months
    else if (
      newEndDate.year() === newStartDate.year() &&
      newEndDate.month() < newStartDate.month()
    ) {
      return newStartDate.clone();
    }

    return newEndDate;
  };

  // Update handlers to ensure they're properly setting formik values too
  const handleStartMonthChange = (e) => {
    const value = parseInt(e.target.value);

    // If no start year is selected, set it to current year
    const currentStartYear = formik?.values?.metadata?.start_year || moment().year();

    const newStartDate = selectedRange.startDate
      .clone()
      .month(value - 1)
      .year(currentStartYear);

    // Validate and adjust end date if needed
    const validatedEndDate = validateDateRange(newStartDate, selectedRange.endDate);

    // Update both the selectedRange and formik values
    setRangeIfChanged(newStartDate, validatedEndDate);

    // Update formik values if formik is available
    if (formik && formik.setFieldValue) {
      formik.setFieldValue("metadata.start_month", value);
      formik.setFieldValue("metadata.start_year", currentStartYear);

      // If end date is not set, mirror the start date
      if (!formik.values.metadata.end_month || !formik.values.metadata.end_year) {
        formik.setFieldValue("metadata.end_month", value);
        formik.setFieldValue("metadata.end_year", currentStartYear);
      }
      // Otherwise just update if they were adjusted
      else if (!validatedEndDate.isSame(selectedRange.endDate)) {
        formik.setFieldValue("metadata.end_month", validatedEndDate.month() + 1);
        formik.setFieldValue("metadata.end_year", validatedEndDate.year());
      }
    }
  };

  const handleStartYearChange = (e) => {
    const value = parseInt(e.target.value);

    // If no start month is selected, set it to current month
    const currentStartMonth = formik?.values?.metadata?.start_month || moment().month() + 1;

    const newStartDate = selectedRange.startDate
      .clone()
      .year(value)
      .month(currentStartMonth - 1);

    // Validate and adjust end date if needed
    const validatedEndDate = validateDateRange(newStartDate, selectedRange.endDate);

    // Update both the selectedRange and formik values
    setSelectedRange({
      startDate: newStartDate,
      endDate: validatedEndDate,
    });

    // Update formik values if formik is available
    if (formik && formik.setFieldValue) {
      formik.setFieldValue("metadata.start_year", value);
      formik.setFieldValue("metadata.start_month", currentStartMonth);

      // If end date is not set, mirror the start date
      if (!formik.values.metadata.end_month || !formik.values.metadata.end_year) {
        formik.setFieldValue("metadata.end_month", currentStartMonth);
        formik.setFieldValue("metadata.end_year", value);
      }
      // Otherwise just update if they were adjusted
      else if (!validatedEndDate.isSame(selectedRange.endDate)) {
        formik.setFieldValue("metadata.end_month", validatedEndDate.month() + 1);
        formik.setFieldValue("metadata.end_year", validatedEndDate.year());
      }
    }
  };

  const handleEndMonthChange = (e) => {
    const value = parseInt(e.target.value);

    // If no start date is set, set it to be same as end date
    if (!formik.values.metadata.start_month || !formik.values.metadata.start_year) {
      // Set the start date to same as end date
      const currentEndYear = formik?.values?.metadata?.end_year || moment().year();
      formik.setFieldValue("metadata.start_month", value);
      formik.setFieldValue("metadata.start_year", currentEndYear);

      // Update selectedRange start date
      setSelectedRange((prev) => ({
        ...prev,
        startDate: moment()
          .year(currentEndYear)
          .month(value - 1),
      }));
    }

    // If no end year is selected, set it to current year
    const currentEndYear = formik?.values?.metadata?.end_year || moment().year();

    const newEndDate = selectedRange.endDate
      .clone()
      .month(value - 1)
      .year(currentEndYear);

    // Validate the new end date against start date
    const validatedEndDate = validateDateRange(selectedRange.startDate, newEndDate);

    // Update both the selectedRange and formik values
    setRangeIfChanged(selectedRange.startDate, validatedEndDate);

    // Update formik values if formik is available
    if (formik && formik.setFieldValue) {
      formik.setFieldValue("metadata.end_month", validatedEndDate.month() + 1);
      formik.setFieldValue("metadata.end_year", currentEndYear);
    }
  };

  const handleEndYearChange = (e) => {
    const value = parseInt(e.target.value);

    // If no end month is selected, set it to current month
    const currentEndMonth = formik?.values?.metadata?.end_month || moment().month() + 1;

    // If no start date is set, set it to be same as end date
    if (!formik.values.metadata.start_month || !formik.values.metadata.start_year) {
      // Set the start date to same as end date
      formik.setFieldValue("metadata.start_month", currentEndMonth);
      formik.setFieldValue("metadata.start_year", value);

      // Update selectedRange start date
      setSelectedRange((prev) => ({
        ...prev,
        startDate: moment()
          .year(value)
          .month(currentEndMonth - 1),
      }));
    }

    const newEndDate = selectedRange.endDate
      .clone()
      .year(value)
      .month(currentEndMonth - 1);

    // Validate the new end date against start date
    const validatedEndDate = validateDateRange(selectedRange.startDate, newEndDate);

    // Update both the selectedRange and formik values
    setSelectedRange({
      ...selectedRange,
      endDate: validatedEndDate,
    });

    // Update formik values if formik is available
    if (formik && formik.setFieldValue) {
      formik.setFieldValue("metadata.end_year", validatedEndDate.year());
      formik.setFieldValue("metadata.end_month", currentEndMonth);
    }
  };

  return (
    <div className="qi-month-year-range-picker">
      <div className="qi-month-year-range-picker__grid">
        {/* Start Date Section */}
        <div className="qi-month-year-range-picker__date-section">
          <label className="qi-month-year-range-picker__date-label">
            <SvgIcon svgClass="qi-month-year-range-picker__icon" name="date-range" />
            {getLocalizedString("start_date", "Start Date")}
          </label>
          <div className="qi-month-year-range-picker__fields">
            <div className="qi-month-year-range-picker__field">
              <label className="qi-month-year-range-picker__field-label">
                {getLocalizedString("month", "Month")}
              </label>
              <select
                className={`qi-month-year-range-picker__select ${
                  startMonthError ? "error-border" : ""
                }`}
                value={formik?.values?.metadata?.start_month || ""}
                onChange={handleStartMonthChange}
              >
                {months?.map((month, index) => (
                  <option value={month.value} key={index}>
                    {getLocalizedString(month?.localization_key) || month?.name}
                  </option>
                ))}
              </select>
              {startMonthError && <div className="error-message">{startMonthError}</div>}
            </div>

            <div className="qi-month-year-range-picker__field">
              <label className="qi-month-year-range-picker__field-label">
                {getLocalizedString("year", "Year")}
              </label>
              <select
                className={`qi-month-year-range-picker__select ${
                  startYearError ? "error-border" : ""
                }`}
                value={formik?.values?.metadata?.start_year || ""}
                onChange={handleStartYearChange}
              >
                {years.map((year) => (
                  <option value={year} key={year}>
                    {year}
                  </option>
                ))}
              </select>
              {startYearError && <div className="error-message">{startYearError}</div>}
            </div>
          </div>
        </div>

        {/* End Date Section */}
        <div className="qi-month-year-range-picker__date-section">
          <label className="qi-month-year-range-picker__date-label">
            <SvgIcon svgClass="qi-month-year-range-picker__icon" name="date-range" />
            {getLocalizedString("end_date", "End Date")}
          </label>
          <div className="qi-month-year-range-picker__fields">
            <div className="qi-month-year-range-picker__field">
              <label className="qi-month-year-range-picker__field-label">
                {getLocalizedString("month", "Month")}
              </label>
              <select
                className={`qi-month-year-range-picker__select ${
                  endMonthError ? "error-border" : ""
                }`}
                value={formik?.values?.metadata?.end_month || ""}
                onChange={handleEndMonthChange}
              >
                {months?.map((month, index) => (
                  <option value={month.value} key={index}>
                    {getLocalizedString(month?.localization_key) || month?.name}
                  </option>
                ))}
              </select>
              {endMonthError && <div className="error-message">{endMonthError}</div>}
            </div>

            <div className="qi-month-year-range-picker__field">
              <label className="qi-month-year-range-picker__field-label">
                {getLocalizedString("year", "Year")}
              </label>
              <select
                className={`qi-month-year-range-picker__select ${
                  endYearError ? "error-border" : ""
                }`}
                value={formik?.values?.metadata?.end_year || ""}
                onChange={handleEndYearChange}
              >
                {years.map((year) => (
                  <option value={year} key={year}>
                    {year}
                  </option>
                ))}
              </select>
              {endYearError && <div className="error-message">{endYearError}</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
